/**
 * Frontend taxonomy — display labels only.
 *
 * This is a MIRROR of TAXONOMY_TREE in server/services/productTaxonomy.js,
 * which is the single source of truth for classification. Never add a category
 * here that the backend classifier cannot produce: the filter would return a
 * permanently empty page. Add it to the backend taxonomy first, then mirror it.
 *
 * See "Classification accuracy — rules of the road" in CATALOGUE_ARCHITECTURE.md.
 */

export const TAXONOMY_TREE = {
    Tops: {
        label: 'Tops',
        subcategories: [
            { key: 'T-Shirts',   label: 'T-Shirts & Shirts' },
            { key: 'Hoodies',    label: 'Hoodies & Sweatshirts' },
            { key: 'Sweaters',   label: 'Knitwear & Jumpers' },
            { key: 'Activewear', label: 'Activewear' },
        ],
    },
    Bottoms: {
        label: 'Bottoms',
        subcategories: [
            { key: 'Jeans',  label: 'Jeans & Denim' },
            { key: 'Pants',  label: 'Trousers & Pants' },
            { key: 'Shorts', label: 'Shorts' },
            { key: 'Skirts', label: 'Skirts' },
        ],
    },
    Dresses: {
        label: 'Dresses',
        subcategories: [
            { key: 'Dresses', label: 'Dresses & Playsuits' },
        ],
    },
    Outerwear: {
        label: 'Outerwear',
        subcategories: [
            { key: 'Jackets', label: 'Jackets & Coats' },
        ],
    },
    Footwear: {
        label: 'Footwear',
        subcategories: [
            { key: 'Shoes', label: 'Shoes & Sneakers' },
        ],
    },
    Swimwear: {
        label: 'Swimwear',
        subcategories: [
            { key: 'Swimwear', label: 'Swimwear & Beachwear' },
        ],
    },
    Accessories: {
        label: 'Accessories',
        subcategories: [
            { key: 'Accessories', label: 'Bags, Hats & More' },
        ],
    },
};

/** Ordered primary category keys for rendering */
export const PRIMARY_CATEGORIES = [
    'Tops', 'Bottoms', 'Dresses', 'Outerwear', 'Footwear', 'Swimwear', 'Accessories',
];

/**
 * All canonical subcategory keys (DB `category` field values).
 */
export const CANONICAL_CATEGORIES = PRIMARY_CATEGORIES.flatMap(
    p => TAXONOMY_TREE[p].subcategories.map(s => s.key)
);

/**
 * Returns true if all subcategories of a primary are selected.
 */
export function isPrimaryFullySelected(primaryKey, selectedKeys) {
    const subs = TAXONOMY_TREE[primaryKey]?.subcategories || [];
    return subs.length > 0 && subs.every(s => selectedKeys.includes(s.key));
}

/**
 * Returns true if some (but not all) subcategories of a primary are selected.
 */
export function isPrimaryPartiallySelected(primaryKey, selectedKeys) {
    const subs = TAXONOMY_TREE[primaryKey]?.subcategories || [];
    const count = subs.filter(s => selectedKeys.includes(s.key)).length;
    return count > 0 && count < subs.length;
}

/**
 * Toggles a primary category: if fully selected, deselects all; otherwise selects all.
 * Returns the new selectedKeys array.
 */
export function togglePrimaryCategory(primaryKey, selectedKeys) {
    const subs = TAXONOMY_TREE[primaryKey]?.subcategories.map(s => s.key) || [];
    const fullySelected = subs.every(k => selectedKeys.includes(k));
    if (fullySelected) {
        return selectedKeys.filter(k => !subs.includes(k));
    }
    const merged = new Set([...selectedKeys, ...subs]);
    return [...merged];
}

/**
 * Returns the primary category key for a given canonical subcategory key.
 */
export function getPrimaryForSubcategory(canonicalKey) {
    for (const [primary, node] of Object.entries(TAXONOMY_TREE)) {
        if (node.subcategories.some(s => s.key === canonicalKey)) return primary;
    }
    return null;
}
